import React, { useEffect, useState } from 'react';
import {
    Box, Typography, Paper, TextField, MenuItem,
    Button, Divider, CircularProgress
} from '@mui/material';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import ArticleIcon from '@mui/icons-material/Article';
import { TaxService } from '../../services/tax.service';
import { ITaxYear, ITaxReportMonthly, IEmployeeTaxSummary } from '../../interfaces/tax.interface';
import { AuthService } from '../../services/auth.service';
import { useToast } from '../../context/ToastContext';
import { useTranslation } from 'react-i18next';

type MonthNumber = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12;
const MONTH_NUMBERS: MonthNumber[] = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12];

const TaxReports: React.FC = () => {
    const { t } = useTranslation(['tax', 'common']);
    const { showError } = useToast();
    const institutionId = AuthService.getSelectedInstitution()!;
    const [taxYears, setTaxYears] = useState<ITaxYear[]>([]);
    const [selectedYearId, setSelectedYearId] = useState('');
    const [monthlyYear, setMonthlyYear] = useState(new Date().getFullYear());
    const [monthlyMonth, setMonthlyMonth] = useState(new Date().getMonth() + 1);
    const [monthlySummary, setMonthlySummary] = useState<ITaxReportMonthly | null>(null);
    const [selectedEmployee, setSelectedEmployee] = useState('');
    const [employees, setEmployees] = useState<IEmployeeTaxSummary[]>([]);
    const [loadingMonthly, setLoadingMonthly] = useState(false);

    useEffect(() => {
        TaxService.getTaxYears(institutionId)
            .then(years => {
                setTaxYears(years);
                const active = years.find(y => y.isActive) ?? years[0];
                if (active) setSelectedYearId(active.id!);
            })
            .catch(() => showError(t('shared.loadTaxYearsFailed')));
    }, [institutionId]);

    useEffect(() => {
        if (!selectedYearId) return;
        TaxService.getEmployeeTaxSummary(selectedYearId, institutionId)
            .then(setEmployees)
            .catch(() => showError(t('reports.toast.loadEmployeesFailed')));
    }, [selectedYearId, institutionId]);

    const loadMonthly = async () => {
        if (!selectedYearId) return;
        setLoadingMonthly(true);
        try {
            const s = await TaxService.getMonthlySummary(selectedYearId, monthlyYear, monthlyMonth, institutionId);
            setMonthlySummary(s);
        } catch { showError(t('reports.toast.loadMonthlyFailed')); }
        finally { setLoadingMonthly(false); }
    };

    const downloadMonthlyPdf = async () => {
        try {
            const blob = await TaxService.downloadMonthlySummaryPdf(selectedYearId, monthlyYear, monthlyMonth, institutionId);
            triggerDownload(blob, `tax-summary-${monthlyYear}-${monthlyMonth}.pdf`);
        } catch { showError(t('shared.downloadFailed')); }
    };

    const downloadCertificate = async () => {
        if (!selectedEmployee) return;
        try {
            const blob = await TaxService.downloadCertificatePdf(selectedYearId, selectedEmployee, institutionId);
            triggerDownload(blob, `tax-certificate-${selectedEmployee}.pdf`);
        } catch { showError(t('shared.downloadFailed')); }
    };

    const triggerDownload = (blob: Blob, filename: string) => {
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
        URL.revokeObjectURL(url);
    };

    return (
        <Box p={3}>
            <Typography variant="h5" mb={3}>{t('reports.title')}</Typography>
            <Box display="flex" gap={3} flexWrap="wrap">

                {/* Monthly Summary */}
                <Paper sx={{ flex: 1, minWidth: 300, p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <CalendarMonthIcon color="primary" />
                        <Typography variant="h6">{t('reports.monthlyTitle')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('reports.monthlyIntro')}
                    </Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <TextField select size="small" label={t('shared.taxYear')} value={selectedYearId}
                            onChange={e => setSelectedYearId(e.target.value)} sx={{ width: 140 }}>
                            {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label={t('common:fields.month')} value={monthlyMonth}
                            onChange={e => setMonthlyMonth(parseInt(e.target.value))} sx={{ width: 130 }}>
                            {MONTH_NUMBERS.map(m => <MenuItem key={m} value={m}>{t(`common:months.${m}`)}</MenuItem>)}
                        </TextField>
                        <TextField size="small" label={t('common:fields.year')} type="number" value={monthlyYear}
                            onChange={e => setMonthlyYear(parseInt(e.target.value))} sx={{ width: 90 }} />
                    </Box>
                    <Button variant="outlined" size="small" onClick={loadMonthly} sx={{ mb: 2 }}>{t('reports.load')}</Button>

                    {loadingMonthly && <CircularProgress size={20} />}
                    {monthlySummary && (
                        <Box sx={{ background: '#f8fafc', borderRadius: 1, p: 2, mb: 2 }}>
                            {[
                                [t('shared.totalEmployees'), monthlySummary.totalEmployees],
                                [t('reports.totalGrossEarnings'), monthlySummary.totalGrossEarnings?.toLocaleString()],
                                [t('reports.totalExemptions'), monthlySummary.totalExemptions?.toLocaleString()],
                            ].map(([k, v]) => (
                                <Box key={k as string} display="flex" justifyContent="space-between" py={0.5}>
                                    <Typography variant="body2" color="text.secondary">{k}</Typography>
                                    <Typography variant="body2" fontWeight={500}>{v}</Typography>
                                </Box>
                            ))}
                            <Divider sx={{ my: 0.5 }} />
                            <Box display="flex" justifyContent="space-between">
                                <Typography variant="body2" color="primary.main" fontWeight={700}>{t('shared.totalTaxDeducted')}</Typography>
                                <Typography variant="body2" color="primary.main" fontWeight={700}>
                                    {monthlySummary.totalTaxDeducted?.toLocaleString()}
                                </Typography>
                            </Box>
                        </Box>
                    )}
                    <Button variant="contained" size="small" onClick={downloadMonthlyPdf}>{t('shared.downloadPdf')}</Button>
                </Paper>

                {/* Annual Certificate */}
                <Paper sx={{ flex: 1, minWidth: 300, p: 3 }}>
                    <Box display="flex" alignItems="center" gap={1} mb={1}>
                        <ArticleIcon color="primary" />
                        <Typography variant="h6">{t('reports.certificateTitle')}</Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" mb={2}>
                        {t('reports.certificateIntro')}
                    </Typography>
                    <Box display="flex" gap={1} mb={2} flexWrap="wrap">
                        <TextField select size="small" label={t('shared.taxYear')} value={selectedYearId}
                            onChange={e => setSelectedYearId(e.target.value)} sx={{ width: 140 }}>
                            {taxYears.map(y => <MenuItem key={y.id} value={y.id}>{y.label}</MenuItem>)}
                        </TextField>
                        <TextField select size="small" label={t('common:fields.employee')} value={selectedEmployee}
                            onChange={e => setSelectedEmployee(e.target.value)} sx={{ width: 180 }}>
                            {employees.map(e => <MenuItem key={e.userId} value={e.userId}>{e.userName}</MenuItem>)}
                        </TextField>
                    </Box>
                    {selectedEmployee && (() => {
                        const emp = employees.find(e => e.userId === selectedEmployee);
                        return emp ? (
                            <Box sx={{ background: '#f8fafc', borderRadius: 1, p: 2, mb: 2 }}>
                                {[
                                    [t('shared.taxableIncomeYtd'), emp.taxableIncomeYtd?.toLocaleString()],
                                    [t('shared.totalTaxDeducted'), emp.taxDeductedYtd?.toLocaleString()],
                                ].map(([k, v]) => (
                                    <Box key={k as string} display="flex" justifyContent="space-between" py={0.5}>
                                        <Typography variant="body2" color="text.secondary">{k}</Typography>
                                        <Typography variant="body2" fontWeight={500}>{v}</Typography>
                                    </Box>
                                ))}
                            </Box>
                        ) : null;
                    })()}
                    <Button variant="contained" size="small" onClick={downloadCertificate} disabled={!selectedEmployee}>
                        {t('shared.downloadPdf')}
                    </Button>
                </Paper>
            </Box>
        </Box>
    );
};

export default TaxReports;
